// Shared goal-library store + UI — used by BOTH the wizard's suggestion cards
// and the editor's goal cards. Persists via window.__PCP_LIBRARY__ (PCPDesk:
// OneDrive-synced SQLite; plain browser: localStorage). Two kinds of entries
// share the store: "overlays" (edits to built-in suggestions, keyed
// "<group>|<heading>" / "custom|…") and "library goals" (serial PCP XXX.YY,
// keyed "lib/<serial>", plus a "lib/_next" counter).
//
// Goal families store vendors as references to the vendor library: [{ vendorId }].
// Legacy data (pre-task-010) stored full vendor objects; libFamily() migrates
// them to vendorId references at read time.

import { vendorAll, vendorAdd, vendorGet } from "vendor_library"

const GOAL_OVERRIDE_LS = "pcp_goal_overrides"
const LIB_PREFIX = "lib/"
const LIB_NEXT   = "lib/_next"

export function goalLibrary() { return (typeof window !== "undefined") ? window.__PCP_LIBRARY__ : null }

export function readGoalOverrides() {
  const lib = goalLibrary()
  if (lib && typeof lib.getAll === "function") {
    try { return JSON.parse(lib.getAll() || "{}") || {} } catch (_) { return {} }
  }
  try { return JSON.parse(localStorage.getItem(GOAL_OVERRIDE_LS) || "{}") || {} } catch (_) { return {} }
}
export function writeGoalOverride(key, obj) {
  const lib = goalLibrary()
  if (lib && typeof lib.save === "function") { lib.save(key, JSON.stringify(obj)); return }
  const all = readGoalOverrides(); all[key] = obj
  try { localStorage.setItem(GOAL_OVERRIDE_LS, JSON.stringify(all)) } catch (_) {}
}
export function deleteGoalOverride(key) {
  const lib = goalLibrary()
  if (lib && typeof lib.remove === "function") { lib.remove(key); return }
  const all = readGoalOverrides(); delete all[key]
  try { localStorage.setItem(GOAL_OVERRIDE_LS, JSON.stringify(all)) } catch (_) {}
}
export function goalOverrideKey(s) { return `${(s && s.group) || ""}|${(s && s.heading) || ""}` }

const _pad = (n, w) => String(n).padStart(w, "0")
export function libSerial(xxx, yy) { return `PCP ${_pad(xxx, 3)}.${_pad(yy, 2)}` }

export function libAll() {
  const all = readGoalOverrides()
  const goals = []
  for (const [k, v] of Object.entries(all)) {
    if (k.startsWith(LIB_PREFIX) && k !== LIB_NEXT && v && typeof v === "object" && v.serial) goals.push(v)
  }
  return goals
}
export function libAllSorted() { return libAll().sort((a, b) => (a.xxx - b.xxx) || (a.yy - b.yy)) }
export function libNext() {
  const m = readGoalOverrides()[LIB_NEXT]
  return (m && typeof m.next === "number" && m.next > 0) ? m.next : 1
}
export function libVariations(xxx) { return libAll().filter(g => g.xxx === xxx).map(g => g.yy) }

// Reusable fields captured into the library — NEVER vendor / frequency (per-plan).
export function libFieldsFrom(s) {
  return {
    name:        (s.heading || s.name || "").trim(),
    category:    s.group || s.category || "other",
    domain:      (s.domain || "").trim(),
    timeline:    (s.timeline || "").trim(),
    description: s.description || "",
    source:      (s.source || "").trim(),
    icon:        s.icon || "📗",
  }
}
function _libSaveGoal(xxx, yy, s) {
  const serial = libSerial(xxx, yy)
  writeGoalOverride(LIB_PREFIX + serial, Object.assign({ serial, xxx, yy }, libFieldsFrom(s)))
  return serial
}
export function libAddNewGoal(s) {
  const xxx = libNext()
  const serial = _libSaveGoal(xxx, 1, s)
  writeGoalOverride(LIB_NEXT, { next: xxx + 1 })
  return serial
}
export function libAddVersion(xxx, s) {
  const yys = libVariations(xxx)
  return _libSaveGoal(xxx, (yys.length ? Math.max(...yys) : 0) + 1, s)
}
export function libDelete(serial) { deleteGoalOverride(LIB_PREFIX + serial) }

// ---- Goal FAMILY (XXX) data — shared across all variations (YY) ----
// References (DDS directives / citations) and a candidate vendor list belong to
// the goal, not a single variation, so they live keyed by XXX ("libfam/<xxx>").
const LIBFAM_PREFIX = "libfam/"
export function xxxOfSerial(serial) { const m = String(serial || "").match(/PCP\s+(\d+)/); return m ? parseInt(m[1], 10) : null }

// Check if a vendor entry is in legacy shape (has name) vs migrated shape (has vendorId)
function _isLegacyVendor(v) {
  return v && typeof v === "object" && typeof v.name === "string" && v.name.trim() && !v.vendorId
}

// Find or create a vendor in the vendor library by case-insensitive name match.
// Returns the vendor id. If the vendor already exists, does NOT update it.
function _findOrCreateVendor(legacy) {
  const name = (legacy.name || "").trim()
  if (!name) return null
  const nameLower = name.toLowerCase()

  // Search existing vendors by name (case-insensitive)
  const all = vendorAll()
  const existing = all.find(v => (v.name || "").toLowerCase() === nameLower)
  if (existing) return existing.id

  // Create new vendor with fields from legacy entry
  return vendorAdd({
    name,
    phone:   (legacy.phone || "").trim(),
    email:   (legacy.email || "").trim(),
    website: (legacy.website || "").trim(),
  })
}

// Migrate legacy vendor entries to vendorId references. Returns the migrated
// vendors array and a flag indicating if any migration occurred.
function _migrateVendors(vendors) {
  if (!Array.isArray(vendors) || vendors.length === 0) return { vendors: [], migrated: false }

  let migrated = false
  const out = []

  for (const v of vendors) {
    if (_isLegacyVendor(v)) {
      const vendorId = _findOrCreateVendor(v)
      if (vendorId) {
        out.push({ vendorId })
        migrated = true
      }
      // If no vendorId (empty name), skip the entry entirely
    } else if (v && v.vendorId) {
      // Already migrated
      out.push({ vendorId: v.vendorId })
    }
    // Skip any other malformed entries
  }

  return { vendors: out, migrated }
}

export function libFamily(xxx) {
  const f = readGoalOverrides()[LIBFAM_PREFIX + xxx]
  const references = (f && Array.isArray(f.references)) ? f.references : []
  const rawVendors = (f && Array.isArray(f.vendors)) ? f.vendors : []

  // Migrate legacy vendors to vendorId shape if needed
  const { vendors, migrated } = _migrateVendors(rawVendors)

  // Write back once if migration occurred
  if (migrated) {
    writeGoalOverride(LIBFAM_PREFIX + xxx, { references, vendors })
  }

  return { references, vendors }
}

// Resolve vendor IDs to full vendor objects from the vendor library.
// Returns vendors with all fields (name, phone, email, website, etc).
// Missing vendors (deleted from library) are filtered out.
export function libFamilyVendorsResolved(xxx) {
  const fam = libFamily(xxx)
  return fam.vendors
    .map(v => vendorGet(v.vendorId))
    .filter(v => v != null)
}
export function libFamilyForSerial(serial) { const x = xxxOfSerial(serial); return x != null ? libFamily(x) : { references: [], vendors: [] } }
export function libFamilySet(xxx, fam) {
  writeGoalOverride(LIBFAM_PREFIX + xxx, {
    references: Array.isArray(fam.references) ? fam.references : [],
    vendors:    Array.isArray(fam.vendors)    ? fam.vendors    : [],
  })
}
const _refKey    = (r) => String((r && (r.title || r.chip)) || "").trim().toLowerCase()
const _vendorKey = (v) => String((v && v.name) || "").trim().toLowerCase()
function _mergeBy(existing, incoming, keyFn) {
  const out = Array.isArray(existing) ? existing.slice() : []
  const seen = new Set(out.map(keyFn).filter(Boolean))
  ;(incoming || []).forEach(item => { const k = keyFn(item); if (k && !seen.has(k)) { seen.add(k); out.push(item) } })
  return out
}
export function libGet(serial) { const g = readGoalOverrides()[LIB_PREFIX + serial]; return (g && typeof g === "object") ? g : null }
// Edit a library goal in place — keeps its serial / xxx / yy, replaces the fields.
export function libUpdate(serial, fields) {
  const g = libGet(serial); if (!g) return
  writeGoalOverride(LIB_PREFIX + serial, {
    serial: g.serial, xxx: g.xxx, yy: g.yy,
    name:        (fields.name || fields.heading || "").trim(),
    category:    fields.category || fields.group || g.category || "other",
    domain:      (fields.domain || "").trim(),
    timeline:    (fields.timeline || "").trim(),
    description: fields.description || "",
    source:      fields.source != null ? fields.source : (g.source || ""),
    icon:        g.icon || "📗",
  })
}

// ---- User service library ----
// Custom "Other" services the user saves from the resource Service picker.
// Stored locally (same store as the goal library) so they persist and appear
// as reusable options under the picker's "Other" group.
const SERVICE_LIB_KEY = "services/_list"
// Each saved service carries the category (sub-section) it belongs to, so
// "Add new" can target any sub-section — or a brand-new one. Legacy entries
// (plain strings from the first version) migrate to { name, category:"Other" }.
export function serviceLibList() {
  const v = readGoalOverrides()[SERVICE_LIB_KEY]
  if (!Array.isArray(v)) return []
  return v.map(x => {
    if (typeof x === "string") return x.trim() ? { name: x.trim(), category: "Other" } : null
    if (x && typeof x === "object" && String(x.name || "").trim()) {
      return { name: String(x.name).trim(), category: String(x.category || "Other").trim() || "Other" }
    }
    return null
  }).filter(Boolean)
}
export function serviceLibAdd(name, category) {
  const n = String(name || "").trim()
  if (!n) return false
  const cat = String(category || "Other").trim() || "Other"
  const list = serviceLibList()
  if (list.some(s => s.name.toLowerCase() === n.toLowerCase())) return false
  writeGoalOverride(SERVICE_LIB_KEY, [...list, { name: n, category: cat }])
  return true
}
export function serviceLibRemove(name) {
  const n = String(name || "").trim().toLowerCase()
  writeGoalOverride(SERVICE_LIB_KEY, serviceLibList().filter(s => s.name.toLowerCase() !== n))
}
// Edit a saved service in place (rename and/or move to another sub-section).
export function serviceLibUpdate(oldName, name, category) {
  const on = String(oldName || "").trim().toLowerCase()
  const n = String(name || "").trim()
  if (!n) return false
  const cat = String(category || "Other").trim() || "Other"
  const list = serviceLibList()
  const idx = list.findIndex(s => s.name.toLowerCase() === on)
  if (idx < 0) return false
  if (list.some((s, i) => i !== idx && s.name.toLowerCase() === n.toLowerCase())) return false
  list[idx] = { name: n, category: cat }
  writeGoalOverride(SERVICE_LIB_KEY, list)
  return true
}
// Category suggestions for the "Add new" picker: the built-in sub-sections plus
// any the user has already created.
export function serviceLibCategories() {
  const base = ["General", "Social Rec", "Health and Safety", "Other"]
  return [...new Set([...base, ...serviceLibList().map(s => s.category)])].filter(Boolean)
}
// Replace the whole service library (used by the Excel/CSV round-trip import —
// what's in the sheet becomes the library). Dedupes by name; returns the count.
export function serviceLibReplace(entries) {
  const seen = new Set()
  const out = []
  ;(Array.isArray(entries) ? entries : []).forEach(e => {
    const name = String(e && e.name || "").trim()
    if (!name) return
    const k = name.toLowerCase()
    if (seen.has(k)) return
    seen.add(k)
    out.push({ name, category: String(e.category || "Other").trim() || "Other" })
  })
  writeGoalOverride(SERVICE_LIB_KEY, out)
  return out.length
}

// ---- UI ----

export function libToast(msg) {
  let bar = document.getElementById("goal-lib-toast")
  if (!bar) {
    bar = document.createElement("div"); bar.id = "goal-lib-toast"
    bar.style.cssText = "position:fixed;bottom:20px;left:50%;transform:translateX(-50%);z-index:10001;background:#0f172a;color:#fff;padding:9px 16px;border-radius:9px;font:600 13px/1.2 ui-sans-serif,system-ui;box-shadow:0 8px 24px rgba(0,0,0,.28);opacity:0;transition:opacity .15s;pointer-events:none;"
    document.body.appendChild(bar)
  }
  bar.textContent = msg; bar.style.opacity = "1"
  clearTimeout(bar._t); bar._t = setTimeout(() => { bar.style.opacity = "0" }, 2000)
}

// Save `fields` (a suggestion/goal-like object) to the library. When `fromXxx`
// is a number the goal derives from an existing library family → ASK new-version
// vs new-goal; otherwise save a new goal. Calls onSaved(serial) after saving.
export function saveGoalToLibrary(fields, fromXxx, onSaved) {
  if (!((fields.heading || fields.name || "").trim())) { libToast("Give the goal a heading first."); return }
  // References & candidate vendors belong to the goal FAMILY (XXX), shared across
  // variations — merge whatever the source goal carries into libfam/<xxx>.
  const captureFamily = (serial) => {
    const xxx = xxxOfSerial(serial)
    if (xxx == null) return
    // Drop incomplete (empty-key) items up front so _mergeBy never silently
    // collapses two blank stubs into one.
    const inRefs = (Array.isArray(fields.references) ? fields.references : []).filter(r => _refKey(r))
    const inVend = (Array.isArray(fields.vendors)    ? fields.vendors    : []).filter(v => _vendorKey(v))
    if (!inRefs.length && !inVend.length) return
    const fam = libFamily(xxx)
    if (inRefs.length) fam.references = _mergeBy(fam.references, inRefs, _refKey)
    if (inVend.length) fam.vendors    = _mergeBy(fam.vendors,    inVend, _vendorKey)
    libFamilySet(xxx, fam)
  }
  const finish = (serial) => { captureFamily(serial); if (onSaved) onSaved(serial) }
  if (typeof fromXxx === "number") { _versionPrompt(fields, fromXxx, finish) }
  else { const serial = libAddNewGoal(fields); libToast(`Saved to library as ${serial}`); finish(serial) }
}

function _versionPrompt(fields, xxx, onSaved) {
  const esc = (v) => String(v == null ? "" : v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  const yys = libVariations(xxx)
  const nextYy = _pad((yys.length ? Math.max(...yys) : 0) + 1, 2)
  const newSerial = libSerial(libNext(), 1)
  const overlay = document.createElement("div")
  overlay.setAttribute("role", "dialog"); overlay.setAttribute("aria-modal", "true")
  overlay.style.cssText = "position:fixed;inset:0;z-index:10000;display:flex;align-items:center;justify-content:center;background:rgba(15,23,42,.55);backdrop-filter:blur(2px);font:14px/1.4 'Inter',ui-sans-serif,system-ui,sans-serif;"
  const opt = "display:block;width:100%;text-align:left;padding:12px 14px;margin-top:8px;border:1px solid #cbd5e1;border-radius:10px;background:#fff;color:#0f172a;cursor:pointer;font:inherit;"
  overlay.innerHTML = `
    <div style="width:min(94vw,460px);background:#fff;border-radius:14px;box-shadow:0 24px 48px rgba(0,0,0,.32);padding:18px 20px 20px;">
      <h3 style="margin:0;font-size:16px;font-weight:800;color:#0f172a;">Save to library</h3>
      <p style="margin:6px 0 4px;font-size:12.5px;color:#64748b;">This goal is based on <strong>PCP ${_pad(xxx, 3)}</strong>. Save it as:</p>
      <button type="button" data-role="version" style="${opt}"><strong>New version — ${esc("PCP " + _pad(xxx, 3) + "." + nextYy)}</strong><div style="font-size:12px;color:#64748b;margin-top:2px;">Keep the same goal, add a variation.</div></button>
      <button type="button" data-role="new" style="${opt}"><strong>New goal — ${esc(newSerial)}</strong><div style="font-size:12px;color:#64748b;margin-top:2px;">A separate goal in your library.</div></button>
      <div style="text-align:right;margin-top:14px;"><button type="button" data-role="cancel" style="padding:8px 14px;font:600 13px/1 inherit;background:#f1f5f9;color:#334155;border:1px solid #e2e8f0;border-radius:8px;cursor:pointer;">Cancel</button></div>
    </div>`
  document.body.appendChild(overlay)
  const close = () => { document.removeEventListener("keydown", onKey); overlay.remove() }
  const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); close() } }
  document.addEventListener("keydown", onKey)
  overlay.addEventListener("click", (e) => { if (e.target === overlay) close() })
  overlay.querySelector('[data-role="cancel"]').addEventListener("click", close)
  overlay.querySelector('[data-role="version"]').addEventListener("click", () => { const s = libAddVersion(xxx, fields); close(); libToast(`Saved as ${s}`); if (onSaved) onSaved(s) })
  overlay.querySelector('[data-role="new"]').addEventListener("click", () => { const s = libAddNewGoal(fields); close(); libToast(`Saved as ${s}`); if (onSaved) onSaved(s) })
}
